import React, { Fragment, useEffect, useState } from 'react';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import { Helmet } from 'react-helmet';
import { BLOG_TYPES } from '../../consts/blog-system.consts';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { getInitState } from '../../helpers/spot-slug';
import { escapeString } from '../../helpers/escape-string';
import BlogArticleDesktopLow from '../../components/blog-article-desktop-low';
import { BlogAuthorDetails } from '../../components/blogs/author-details';
import { Banner1, Banner2 } from '../../components/blogs/banners';
import { RouteFormatter } from '../../routes';
import BlogArticleSimple from '../../components/blog-article-simple';
import { BlogSubDetail } from '../../components/blogs/sub-details';
import SeoAnimalShelter from '../../components/seo-animal-shelter';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import BlogCategories from '../../components/blog-categories';
import BlogTopSticky from '../../components/blog-top-nav/top-sticky';
import MobileSubPage from '../../components/mobile-sub-page';
import BlogSideBar from '../../components/blog-side-bar';
import SniffButton from '../../components/sniff-button';
import ModalMobile from '../../components/modal-mobile';
import BlogFooter from '../../components/blog-footer';
import BlogTopNav from '../../components/blog-top-nav';
import PictureSet from '../../components/picture-set';
import Preloader from '../../components/preloader';
import SnifHr from '../../components/snif-hr';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgLoading from 'src/assets/svg/SvgLoading.svg';
import '../blog/post.scss';
import './style.scss';

const FooterNew = loadable(() => import('../../components/footer-new'));

const useGetDogRescueTesti = require('../../services/hooks/useGetDogRescueTesti');
const useGetDogRescuesPage = require('../../services/hooks/useGetDogRescuesPage');
const useGetBlogCategories = require('../../services/hooks/useGetBlogCategories');

const b = bemCn('sniff-blog-post');
const bDog = bemCn('sniff-dog-rescue');

const types = {
    CITY: 'CITY',
    STATE: 'STATE',
    COUNTRY: 'COUNTRY',
    PROFILE: 'PROFILE',
    SPECIALIZATION: 'SPECIALIZATION',
};

const Divider = () => (
    <div className="py-4">
        <SnifHr w100 />
    </div>
);

const RenderListCity = ({ pageType, cities, myTitle }) => {
    const title = myTitle || 'Top Rescues & Shelters By City';

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            {pageType == types.COUNTRY && (
                <p className={bDog('p-lora').mix('mb-2')}>
                    Note: we only include the top 10 cities where we have enough data to compile a solid list
                </p>
            )}
            <ul className={bDog('city-link')}>
                {cities?.map((s, i) => {
                    const splitSlug = s?.slug?.split('-');
                    const shortState = splitSlug.at(-1).toUpperCase();
                    const name = `${s.name || s.city}, ${shortState}`;

                    return (
                        <li key={i}>
                            <a href={RouteFormatter.dogRescueSub({ id: s.slug })} rel={s.noFollow ? 'nofollow' : ''} className="link-style">
                                {name}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

const RenderListSpecialized = ({ myTitle, mySpecs }) => {
    const title = myTitle || 'Dog Rescue & Shelter Types';

    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{title}</h2>
            <ul className={bDog('city-link')}>
                {mySpecs.map((s, i) => (
                    <li key={i}>
                        <a href={RouteFormatter.dogRescueSub({ id: s.slug })} rel={s.noFollow ? 'nofollow' : ''} className="link-style">
                            {s.name || s.specialization}
                        </a>
                    </li>
                ))}
            </ul>
        </>
    );
};

const RenderListRescue = ({ myTitle, otherRescues }) => {
    return (
        <>
            <h2 className="snif-m1 snif-l2-pc snif-semibold mb-2">{myTitle}</h2>
            <ul className={bDog('city-link')}>
                {otherRescues?.map((s, i) => {
                    return (
                        <li key={i}>
                            <a href={s.slug} target="_blank" rel="noopener noreferrer" className="link-style">
                                {s.name || s.rescueName}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

const getBread = ({ city, pageType, query, rescueName, slug, state, specialization }) => {
    const bread = [
        { name: 'Blog', url: RouteFormatter.blog({}) },
        { name: 'Dog Rescues & Shelters', url: RouteFormatter.dogRescue() },
    ];

    if (pageType == types.STATE) {
        bread.push({ name: state, url: RouteFormatter.dogRescueSub({ id: query || slug }) });
    } else if (pageType == types.CITY) {
        const fixState = state?.trim().replaceAll('.', '').replaceAll(' ', '-')?.toLowerCase();
        bread.push({ name: state, url: RouteFormatter.dogRescueSub({ id: fixState }) });
        bread.push({ name: city });
    } else if (pageType == types.SPECIALIZATION) {
        bread.push({ name: specialization });
    } else if (pageType == types.PROFILE) {
        const fixState = state?.trim().replaceAll('.', '').replaceAll(' ', '-')?.toLowerCase();
        const fixCity = city?.trim().replaceAll('.', '').replaceAll(' ', '-')?.toLowerCase();
        const initState = state ? getInitState(state) : '';
        const citySlug = `${fixCity}-${initState?.toLowerCase()}`;
        const canShowCity = !!fixCity && fixCity != 'none';
        fixState && bread.push({ name: state, url: RouteFormatter.dogRescueSub({ id: fixState }) });
        canShowCity && bread.push({ name: city, url: RouteFormatter.dogRescueSub({ id: citySlug }) });
        bread.push({ name: rescueName });
    }

    return bread;
};

const RenderRanks = ({ countryRank, stateRank, cityRank, simple, state, city, specializationRanks }) => {
    const citySlug = city && cityRank && state ? `${escapeString(city).replaceAll('.', '')}-${getInitState(state)?.toLowerCase()}` : '';

    const cRanks = countryRank ? (
        <>
            #{countryRank} in{' '}
            <a href={RouteFormatter.dogRescue()} className="text-underline">
                United States
            </a>
        </>
    ) : null;
    const sRanks =
        state && stateRank ? (
            <>
                #{stateRank} in{' '}
                <a href={RouteFormatter.dogRescueSub({ id: escapeString(state) })} className="text-underline">
                    {state}
                </a>
            </>
        ) : null;

    const cityRanks =
        city && cityRank ? (
            <>
                #{cityRank} in{' '}
                <a href={RouteFormatter.dogRescueSub({ id: citySlug })} className="text-underline">
                    {city}
                </a>
            </>
        ) : null;
    const speRanks = specializationRanks && (
        <>
            {Object.entries(specializationRanks).map(([key, value]) => (
                <>
                    #{value} in{' '}
                    <a href={RouteFormatter.dogRescueSub({ id: escapeString(key) })} className="text-capitalize text-underline">
                        {key}
                    </a>
                    <br />
                </>
            ))}
        </>
    );
    const ranks = [];
    cRanks && ranks.push(cRanks);
    sRanks && ranks.push(sRanks);
    cityRanks && ranks.push(cityRanks);

    if (ranks.length == 0) {
        return '';
    }

    return (
        <p className={simple ? '' : 'text-primary'}>
            {ranks.map((r, i) => (
                <React.Fragment key={i}>
                    {r}
                    {i < ranks.length - 1 ? ', ' : ''}
                </React.Fragment>
            ))}
            <br />
            {speRanks}
        </p>
    );
};

const RenderTesti = ({ t, full }) => {
    const testi = t.testimonial?.split(' ').slice(0, 20);
    const testiText = full ? t.testimonial : `${testi.join(' ')}${testi?.length == 20 ? '...' : ''}`;
    const revNameSplit = t.reviewerName?.split(' ');
    const revName = revNameSplit?.length > 1 ? `${revNameSplit[0]} ${revNameSplit[1][0]}` : t.reviewerName;
    const aff = t.affiliation?.join(', ');

    return (
        <div className={bDog('profile_testimonial')}>
            <p className={bDog('p-lora')}>"{testiText}"</p>
            <p className={bDog('profile_testimonial_name').mix('snif-p snif-semibold mt-2')}>— {revName?.toLowerCase()}.</p>
            <p className="snif-s2 snif-medium text-medium mt-05">{aff}</p>
        </div>
    );
};

const RenderAllTesti = ({ canShowLoadMore, getMoreTesti, loadingGetMoreTesti, myTesties }) => {
    return (
        <div>
            {myTesties.map((t, i) => (
                <RenderTesti key={i} t={t} full />
            ))}
            {canShowLoadMore && (
                <SniffButton color="secondary" size="lg" onClick={getMoreTesti} disabled={loadingGetMoreTesti}>
                    Load more testimonials
                    {loadingGetMoreTesti && <SvgLoading className="ms-1" />}
                </SniffButton>
            )}
        </div>
    );
};

const RenderMobileModal = ({ canShowLoadMore, openTesti, handleOpenTesti, getMoreTesti, loadingGetMoreTesti, myTesties }) => (
    <MobileSubPage title="All testimonials" show={openTesti} onBack={handleOpenTesti}>
        <RenderAllTesti {...{ canShowLoadMore, getMoreTesti, loadingGetMoreTesti, myTesties }} />
    </MobileSubPage>
);

const RenderDesktopModal = ({ canShowLoadMore, openTesti, handleOpenTesti, getMoreTesti, loadingGetMoreTesti, myTesties }) => (
    <ModalMobile title="All testimonials" isOpen={openTesti} toggle={handleOpenTesti}>
        <RenderAllTesti {...{ canShowLoadMore, getMoreTesti, loadingGetMoreTesti, myTesties }} />
    </ModalMobile>
);

const RenderRescueProfile = ({ blogPost, getDogRescueTesti, loadingGetMoreTesti, useStateSSR }) => {
    const isMobile = useMobileScreen();
    const [page, setPage] = useState(1);
    const [openTesti, setOpenTesti] = useState(false);
    const [RenderAllTestiModal, setRenderAllTestiModal] = useState(() => RenderMobileModal);
    const [myTesties, setMyTesties] = useState(blogPost?.testimonials || []);
    const [canShowLoadMore, setCanShowLoadMore] = useState(blogPost?.testimonials?.length == 10);
    const mainTitle = blogPost?.rescueName;
    const logoUrl = ImageHelper.getUrlFromOptions(blogPost?.logo, `fit-in/${IMG_RESIZES.x512}`);
    const web = blogPost?.website || '';
    const fixWeb = web ? (web.startsWith('http') ? web : `http://${web}`) : '';
    const adoptWeb = blogPost?.adoptionWebsite || '';
    const fixAdoptWeb = adoptWeb ? (adoptWeb.startsWith('http') ? adoptWeb : `http://${adoptWeb}`) : '';
    const myCity = !blogPost?.city || blogPost?.city?.toLowerCase() == 'none' ? blogPost?.state : blogPost?.city;
    const resSpecTitle = `See The Best Dog Rescues & Shelters By Specialization In ${myCity}`;
    const recRescuesTitle = `See The Best Dog Rescues & Shelters In ${myCity}`;
    const recCitiesTitle = `See The Best Dog Rescues & Shelters In Other Cities Near ${myCity}`;
    const mySlug = blogPost?.slug?.split('/')[2];

    const handleOpenTesti = () => setOpenTesti((o) => !o);

    const getMoreTesti = async () => {
        try {
            const newPage = page + 1;
            setPage(newPage);
            const res = await getDogRescueTesti({
                variables: {
                    slug: mySlug,
                    page: newPage,
                    limit: 10,
                },
            });
            const newTesti = res?.data?.dogRescuePage?.testimonials;
            newTesti?.length < 10 && setCanShowLoadMore(false);
            newTesti?.length > 0 && setMyTesties((t) => [...t, ...newTesti]);
        } catch (error) {
            console.warn(error);
        }
    };

    const AllTestimonial = (
        <RenderAllTestiModal
            {...{
                canShowLoadMore,
                openTesti,
                handleOpenTesti,
                getMoreTesti,
                loadingGetMoreTesti,
                myTesties,
            }}
        />
    );

    useEffect(() => {
        if (!isMobile) {
            setRenderAllTestiModal(() => RenderDesktopModal);
        }
    }, [isMobile]);

    return (
        <div className="mx-2 mx-md-0">
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_RESCUES.id }} />
            <SeoAnimalShelter {...{ ...blogPost }} />
            <div className="mb-4">
                <div className={bDog('profile-header')}>
                    <PictureSet
                        className={bDog('profile-header_logo')}
                        imgs={logoUrl}
                        width={250}
                        height={250}
                        white
                        alt={`${blogPost?.rescueName} Logo`}
                    />
                    <div>
                        <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc text-capitalize">{mainTitle}</h1>
                        <div className={bDog('profile-header_content')}>
                            <RenderRanks {...{ ...blogPost }} />
                            <p>{blogPost?.address}</p>
                            <p className="text-primary">
                                <a href={fixWeb} target="_blank" rel="noopener noreferrer">
                                    {fixWeb}
                                </a>
                            </p>
                            <p className="text-primary">
                                <a href={`mailto:${blogPost?.publicEmail}`}>{blogPost?.publicEmail}</a>
                            </p>
                            {blogPost?.serviceAreas && (
                                <>
                                    <p className="snif-bold">Service areas:</p>
                                    <p>{blogPost?.serviceAreas}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className={bDog('p-lora').mix('mt-3')} dangerouslySetInnerHTML={{ __html: blogPost?.bio }} />
                {blogPost?.mission && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Mission and Vision</h3>
                        <div className={bDog('p-lora').mix('mt-05')} dangerouslySetInnerHTML={{ __html: blogPost?.mission }} />
                    </>
                )}
                {blogPost?.specializations?.length > 0 && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Specialization</h3>
                        <ul className={bDog('p-lora')}>
                            {blogPost?.specializations?.map((s, i) => (
                                <li key={i}>{s}</li>
                            ))}
                        </ul>
                    </>
                )}
                {blogPost?.otherSpecializations?.length > 0 && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Other specialization</h3>
                        <ul className={bDog('p-lora')}>
                            {blogPost?.otherSpecializations?.map((s, i) => (
                                <li key={i}>{s}</li>
                            ))}
                        </ul>
                    </>
                )}
                {blogPost?.adoptionPolicy && (
                    <>
                        <h3 className={bDog('p-lora').mix('snif-bold mt-3')}>Adoption Policy</h3>
                        <div className={bDog('p-lora').mix('mt-05 mb-1')} dangerouslySetInnerHTML={{ __html: blogPost?.adoptionPolicy }} />
                    </>
                )}
                {fixAdoptWeb && (
                    <a href={fixAdoptWeb} className={bDog('p-lora').mix('text-primary text-underline')} target="_blank" rel="noopener noreferrer">
                        {fixAdoptWeb}
                    </a>
                )}
                <Divider />
                <div className="d-flex justify-content-between align-items-center mb-4" onClick={() => (isMobile ? handleOpenTesti() : {})}>
                    <h2 className="snif-m1 snif-l2-pc snif-semibold">Testimonials</h2>
                    {myTesties.length > 0 && (
                        <>
                            <span className={bDog('p-lora').mix('d-none d-md-block text-primary text-underline c-pointer')} onClick={handleOpenTesti}>
                                See all testimonials
                            </span>
                            <div className={bDog('chev-right')}>
                                <SvgChevronRight />
                            </div>
                        </>
                    )}
                </div>
                {myTesties.length > 0 ? (
                    myTesties.map((t, i) => <RenderTesti key={i} t={t} full />)
                ) : (
                    <p className={bDog('p-lora').mix('text-medium mb-4')}>No testimonials submitted</p>
                )}
                {canShowLoadMore && (
                    <SniffButton color="secondary" size="lg" onClick={getMoreTesti} disabled={loadingGetMoreTesti}>
                        Load more testimonials
                        {loadingGetMoreTesti && <SvgLoading className="ms-1" />}
                    </SniffButton>
                )}
                <Banner1 />

                {blogPost?.specializationsNearby?.length > 0 && (
                    <>
                        <RenderListSpecialized myTitle={resSpecTitle} mySpecs={blogPost.specializationsNearby} />
                        <Divider />
                    </>
                )}
                {blogPost?.otherRescues?.length > 0 && (
                    <>
                        <RenderListRescue myTitle={recRescuesTitle} {...{ ...blogPost }} />
                        <Divider />
                    </>
                )}
                {blogPost?.cities?.length > 0 && (
                    <>
                        <RenderListCity myTitle={recCitiesTitle} {...{ ...blogPost }} />
                        <Banner2 />
                    </>
                )}
            </div>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_RESCUES.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
            {AllTestimonial}
        </div>
    );
};

const updatedAt = 'Nov 4, 2025';

const DogRescue = ({ dataSSR, categoriesSSR, useStateSSR, serverSSR }) => {
    const isMobile = useMobileScreen();
    const { data: clientData, loading } = useStateSSR ? {} : useGetDogRescuesPage.default(window?.location?.pathname?.split('/')[2]);
    const { data: clientCat, loading: loadingCat } = useStateSSR ? {} : useGetBlogCategories.default();
    const { getDogRescueTesti, loading: loadingGetMoreTesti } = serverSSR ? {} : useGetDogRescueTesti.default();
    const blogPost = dataSSR || clientData || {};
    const blogCategories = categoriesSSR || clientCat || [];
    const recentPosts = blogPost?.recentPosts || [];
    const previousPost = blogPost?.previousPost;
    const nextPost = blogPost?.nextPost;
    const listBread = getBread({ ...blogPost });

    const handleGo = (e, slug) => {
        e.preventDefault();
        e.stopPropagation();
        window.location = slug;
    };

    return (
        <>
            {!useStateSSR && (
                <Helmet>
                    <title>{blogPost?.metaTitle}</title>
                    <meta name="description" content={blogPost?.metaDescription} />
                </Helmet>
            )}
            <div className={`${b()} ${bDog()}`}>
                <BlogTopSticky {...{ useStateSSR }} />
                <BlogTopNav />
                <div className="d-none d-md-block b-bot mb-3" />
                <div className="desktop-container">
                    <div className="mx-2 mx-md-0 mt-1">
                        <SpotBreadcrumbs
                            {...{
                                listBread,
                                SSR: !!useStateSSR,
                                general: true,
                            }}
                        />
                    </div>
                </div>
                <div className={b('content').mix('desktop-container')}>
                    {loading || loadingCat ? (
                        <Preloader full />
                    ) : (
                        <RenderRescueProfile {...{ blogPost, getDogRescueTesti, loadingGetMoreTesti, useStateSSR }} />
                    )}
                    <BlogSideBar {...{ blogPost, useStateSSR }} />
                </div>
                <div className="desktop-container">
                    <div className={b('prev-next-post')}>
                        {previousPost && (
                            <div className={b('prev-next-post_prev')} onClick={(e) => handleGo(e, previousPost.slug)}>
                                <SvgChevronLeft />
                                <div>
                                    <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                        <a href={previousPost.slug}>PREVIOUS POST</a>
                                    </p>
                                    <BlogArticleSimple {...{ data: previousPost, mobileSquare: true, useStateSSR }} />
                                </div>
                            </div>
                        )}
                        {nextPost && (
                            <div className={b('prev-next-post_next')} onClick={(e) => handleGo(e, nextPost.slug)}>
                                <div>
                                    <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                                        <a href={nextPost.slug}>NEXT POST</a>
                                    </p>
                                    <BlogArticleSimple {...{ data: nextPost, mobileSquare: true, useStateSSR }} />
                                </div>
                                <SvgChevronRight />
                            </div>
                        )}
                    </div>
                    <div className="mx-2 mx-md-0">
                        <h2 className="snif-m1 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5">Most recent articles</h2>
                        <div className={b('recent')}>
                            {recentPosts.map((v) => (
                                <Fragment key={v.id}>
                                    <BlogArticleSimple data={v} {...{ useStateSSR }} />
                                    <BlogArticleDesktopLow data={v} {...{ useStateSSR }} />
                                </Fragment>
                            ))}
                        </div>
                        <div className="d-md-none">
                            <h5 className="snif-m2 snif-semibold mb-3">Related articles</h5>
                            {blogPost?.relatedPosts?.map((v) => (
                                <BlogArticleSimple key={v.id} data={v} {...{ useStateSSR }} />
                            ))}
                        </div>
                        <h2 className="snif-m1 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5 pt-3 pt-md-0">Top dog guides per area</h2>
                        <BlogCategories categories={blogCategories} />
                    </div>
                </div>
                <BlogFooter {...{ isMobile, useStateSSR }} />
            </div>
            {!useStateSSR && <FooterNew isShowCities={false} />}
        </>
    );
};

export default DogRescue;
